import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { stageClass } from "../lib/api";
import { PlayCircle, LogOut, School } from "lucide-react";

const QUICK_REASONS = {
  "Backed Out": ["Student chose another college", "Financial reasons", "Personal / family reasons", "No response from student"],
  "Withdrawn": ["Visa refused", "Student withdrew request", "Program cancelled by college", "Deferred to a later intake"],
  "Selected Another College": ["Better scholarship elsewhere", "Preferred location", "Lower tuition elsewhere", "Program better fit"],
};

export function StageReasonModal({ stage, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => { setSaving(true); try { await onConfirm(reason.trim()); } finally { setSaving(false); } };
  return (
    <Dialog open={!!stage} onOpenChange={o => { if (!o) onCancel(); }}>
      <DialogContent data-testid="stage-reason-modal" className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="heading-font">Why is this application {stage === "Selected Another College" ? "closed" : stage?.toLowerCase()}?</DialogTitle>
          <DialogDescription>A reason is required to mark it as <b>{stage}</b>. It's saved on the application and in the activity log.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {(QUICK_REASONS[stage] || []).map(q => (
            <button key={q} type="button" data-testid={`stage-reason-quick-${q.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setReason(q)}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${reason === q ? "border-orange-400 bg-orange-50 text-orange-800" : "border-slate-200 hover:border-orange-300 text-slate-700"}`}>{q}</button>
          ))}
        </div>
        <Textarea data-testid="stage-reason-input" value={reason} onChange={e => setReason(e.target.value)} maxLength={1000}
          placeholder="Type the reason…" className="min-h-[90px] text-sm" />
        <DialogFooter>
          <Button variant="outline" data-testid="stage-reason-cancel" onClick={onCancel}>Cancel</Button>
          <Button data-testid="stage-reason-confirm" disabled={!reason.trim() || saving} onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white">
            {saving ? "Saving…" : `Mark ${stage}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TONES = {
  emerald: "hover:border-emerald-400 hover:bg-emerald-50 text-emerald-600",
  zinc: "hover:border-zinc-400 hover:bg-zinc-50 text-zinc-600",
  violet: "hover:border-violet-400 hover:bg-violet-50 text-violet-600",
};

const ACTIONS = [
  { value: "keep", icon: PlayCircle, title: "Keep them in progress", desc: "Continue processing the other applications", tone: "emerald" },
  { value: "Backed Out", icon: LogOut, title: "Mark as Backed Out", desc: "Student is not continuing with them", tone: "zinc" },
  { value: "Selected Another College", icon: School, title: "Selected another college", desc: "Student chose this offer over the others", tone: "violet" },
];

export function SiblingDecisionModal({ open, siblings, onCancel, onChoose }) {
  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onCancel(); }}>
      <DialogContent data-testid="sibling-decision-modal" className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="heading-font">This student has other applications</DialogTitle>
          <DialogDescription>What should happen to the {siblings.length} other application{siblings.length > 1 ? "s" : ""} in progress?</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {siblings.map(s => (
            <div key={s.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
              <span className="mono text-slate-500">{s.application_id}</span>
              <span className="font-medium text-slate-800 truncate flex-1">{s.college_code || s.college} · {s.program}</span>
              <span className={`stage-badge ${stageClass(s.stage)}`}>{s.stage}</span>
            </div>
          ))}
        </div>
        <div className="grid gap-2">
          {ACTIONS.map(({ value, icon: I, title, desc, tone }) => (
            <button key={value} type="button" data-testid={`sibling-action-${value.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => onChoose(value)}
              className={`flex items-center gap-3 rounded-xl border-2 border-slate-200 px-3 py-2.5 text-left transition-colors ${TONES[tone]}`}>
              <I className="w-5 h-5 shrink-0" />
              <div><div className="text-sm font-semibold text-slate-900">{title}</div><div className="text-xs text-slate-500">{desc}</div></div>
            </button>
          ))}
        </div>
        <DialogFooter><Button variant="ghost" data-testid="sibling-decision-cancel" onClick={onCancel}>Cancel</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
